import { PushPermission } from '@/lib/push/pushPermission'
import { PUSH_PROMPT_COOLDOWN_MS, PushPromptPolicy } from '@/lib/push/pushPromptDecision'

/**
 * Whether this device should silently re-establish a push registration.
 *
 * WHY THIS EXISTS. A push registration binds one browser to one signed-in user,
 * so logging out deletes it — correctly, and deliberately: leaving it would keep
 * delivering the previous user's notifications, with the issue title and author
 * visible in the notification body, to whoever holds the machine next. That half
 * of the standard pattern was implemented. The other half was not: nothing ever
 * re-registered on the way back IN, and registration was treated as a one-time
 * onboarding step rather than part of session bootstrap.
 *
 * Measured on production 2026-08-17. The delete fires ~0.6s before /logout:
 *   05:14:22 POST   /push_devices    201
 *   05:14:31 DELETE /push_devices/6  204
 *   05:14:32 GET    /logout          302
 * After that the row is gone, and because PushDevice exposes no Get operation
 * (Post/Patch/Delete only), both the settings screen and the prompt gate read the
 * local marker instead of the server — so the user is shown "push off" and is
 * asked to enable it again, every single login.
 *
 * WHAT THIS IS NOT. It does not weaken the shared-machine guarantee. Logout still
 * deletes, and a registration is only ever created for the user who is signed in
 * at that moment, so the departing user's notifications never follow them.
 */
export type PushAutoRegisterInput = {
  /** An authenticated user is loaded (not merely a valid session). */
  hasUser: boolean
  /** Registration POSTs /push_devices, so offline it could only fail. */
  isOnline: boolean
  permission: PushPermission
  /** `isPushRegisteredOnThisDevice(userId)` — THIS user already registered here. */
  isRegisteredOnThisDevice: boolean
  /** This user's persisted prompt policy on this device. */
  policy: PushPromptPolicy
  /**
   * The server-side answer to "does this person want push at all", derived from
   * NotificationPreference. `null` while it is still loading or could not be
   * fetched — which must NOT be read as "yes".
   */
  wantsPush: boolean | null
  /**
   * A request dominated by server work is in flight (`useServerWorkStore`).
   * Registering now would put POST /push_devices behind it — on 2026-10-16 it
   * waited on a report render's PHP session lock and timed out — and this path
   * makes ONE attempt per login, so losing it to a render means no push until
   * the next login.
   */
  isServerWorkInFlight: boolean
  now: number
}

export type PushAutoRegisterSkipReason =
  | 'no-user'
  | 'offline'
  | 'permission-not-granted'
  | 'already-registered'
  | 'dismissed-forever'
  | 'within-cooldown'
  | 'preference-unknown'
  | 'preference-off'
  | 'server-work-in-flight'

export type PushAutoRegisterDecision =
  | { register: true }
  | { register: false; reason: PushAutoRegisterSkipReason }

/**
 * Pure on purpose, exactly like decidePushPrompt: every branch is reachable from
 * explicit inputs, so the policy can be pinned without mounting a component,
 * faking a platform or issuing a request.
 */
export function decidePushAutoRegister(input: PushAutoRegisterInput): PushAutoRegisterDecision {
  if (!input.hasUser) return { register: false, reason: 'no-user' }
  if (!input.isOnline) return { register: false, reason: 'offline' }

  // Only `granted`. This path must never surface an OS dialog: an unprompted
  // permission request on app start is the pattern browsers punish and users
  // refuse. Asking is the soft prompt's job, behind a deliberate tap.
  if (input.permission !== 'granted') {
    return { register: false, reason: 'permission-not-granted' }
  }

  if (input.isRegisteredOnThisDevice) return { register: false, reason: 'already-registered' }

  // LOAD-BEARING. `dismissedForever` is not only "stop nagging me" — pressing
  // Disable in notification settings sets it too (NotificationPreferences.tsx),
  // and Disable does NOT clear the server-side preference. So the server can
  // still say "push on for mentions" for someone who explicitly turned push off
  // on this device. Without this branch, auto-registration would resurrect push
  // against an explicit decision on the next login — a worse defect than the one
  // being fixed, because the user already told us no.
  if (input.policy.dismissedForever) return { register: false, reason: 'dismissed-forever' }

  // Honour the snooze too, on the SAME cooldown decidePushPrompt uses. "Not now"
  // is a deferral of the decision, so completing that decision silently is the
  // one thing it rules out. Two mechanisms reading one policy must not disagree:
  // without this, the card politely stops asking for a week while registration
  // happens anyway behind it — the user's answer honoured in appearance and
  // reversed in effect.
  if (
    input.policy.lastSnoozedAt !== null &&
    input.now - input.policy.lastSnoozedAt < PUSH_PROMPT_COOLDOWN_MS
  ) {
    return { register: false, reason: 'within-cooldown' }
  }

  // Absent is not consent. A failed or pending preference fetch means we do not
  // know, and registering on "do not know" would silently opt people in.
  if (input.wantsPush === null) return { register: false, reason: 'preference-unknown' }
  if (!input.wantsPush) return { register: false, reason: 'preference-off' }

  // Last: a deferral, never a refusal. The effect re-runs when the work settles.
  if (input.isServerWorkInFlight) return { register: false, reason: 'server-work-in-flight' }

  return { register: true }
}
